import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { cigarApi } from './cigarApi'
import { humidorApi } from './humidorApi'
import { historyApi } from './historyApi'

export interface SmokingSession {
  id: string
  userId: string
  cigarId: string
  smokedDate: string
  startTime?: string
  endTime?: string
  durationMinutes?: number
  rating?: number
  tastingNotes?: string
  pairing?: string
  location?: string
  weather?: string
  temperature?: number
  humidity?: number
  setting?: string
  companions?: string[]
  preSmokingNotes?: string
  drawQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  burnQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  ashColor?: 'white' | 'gray' | 'black' | 'mixed'
  imageUrls: string[]
  isDraft?: boolean
  createdAt: string
  updatedAt: string
  sessionRating?: {
    flavor: number
    construction: number
    burn: number
    value: number
    overall: number
    isOverallManual: boolean
    flavorNotes?: string | null
    constructionNotes?: string | null
    burnNotes?: string | null
    valueNotes?: string | null
    overallNotes?: string | null
  } | null
  cigar: {
    id: string
    vitola: { id: string; name: string }
    cigarLine: {
      id: string
      name: string
      brand: {
        id: string
        name: string
      }
    }
  }
}

export interface CreateSessionRequest {
  cigarId: string
  smokedDate?: string
  startTime?: string
  endTime?: string
  durationMinutes?: number
  rating?: number
  tastingNotes?: string
  pairing?: string
  location?: string
  weather?: string
  temperature?: number
  humidity?: number
  setting?: string
  companions?: string[]
  preSmokingNotes?: string
  drawQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  burnQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  ashColor?: 'white' | 'gray' | 'black' | 'mixed'
  imageUrls?: string[]
  isDraft?: boolean
}

export interface UpdateSessionRequest {
  smokedDate?: string
  startTime?: string
  endTime?: string
  durationMinutes?: number
  rating?: number
  tastingNotes?: string
  pairing?: string
  location?: string
  weather?: string
  temperature?: number
  humidity?: number
  setting?: string
  companions?: string[]
  preSmokingNotes?: string
  drawQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  burnQuality?: 'poor' | 'fair' | 'good' | 'excellent'
  ashColor?: 'white' | 'gray' | 'black' | 'mixed'
  imageUrls?: string[]
  isDraft?: boolean
}

export interface SessionSearchParams {
  cigarId?: string
  dateFrom?: string
  dateTo?: string
  search?: string
  sortBy?: 'smokedDate' | 'rating' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
  includeDrafts?: boolean
}

export interface SessionPagination {
  page: number
  limit: number
  totalCount: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface SessionSearchResponse {
  sessions: SmokingSession[]
  totalPages: number
  pagination: SessionPagination
}

export interface SessionStats {
  totalSessions: number
  averageRating: number | null
  totalSmokingTime: number
  averageSessionDuration: number
  recentSessions: SmokingSession[]
}

export const sessionApi = createApi({
  reducerPath: 'sessionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/smoking-sessions',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['SmokingSession'],
  endpoints: (builder) => ({
    getSessions: builder.query<SessionSearchResponse, SessionSearchParams>({
      query: (params) => ({
        url: '',
        params,
      }),
      transformResponse: (response: { data: { sessions: SmokingSession[]; pagination: SessionPagination } }) => ({
        sessions: response.data.sessions,
        pagination: response.data.pagination,
        totalPages: response.data.pagination.totalPages,
      }),
      providesTags: ['SmokingSession'],
    }),
    getSession: builder.query<SmokingSession, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      providesTags: (_result, _error, id) => [{ type: 'SmokingSession', id }],
    }),
    createSession: builder.mutation<SmokingSession, CreateSessionRequest>({
      query: (session) => ({
        url: '',
        method: 'POST',
        body: session,
      }),
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      invalidatesTags: ['SmokingSession'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Invalidate cigar and humidor caches so counts update
          dispatch(cigarApi.util.invalidateTags(['Cigar']))
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
        } catch {
          // mutation failed, no cache invalidation needed
        }
      },
    }),
    updateSession: builder.mutation<SmokingSession, { id: string; updates: UpdateSessionRequest }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      invalidatesTags: ['SmokingSession'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(historyApi.util.invalidateTags(['History']))
        } catch { /* mutation failed */ }
      },
    }),
    deleteSession: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SmokingSession'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Session delete restores cigar quantity; refresh collection counts.
          dispatch(cigarApi.util.invalidateTags(['Cigar']))
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
          dispatch(historyApi.util.invalidateTags(['History']))
        } catch {
          // mutation failed, no cache invalidation needed
        }
      },
    }),
    getSessionStats: builder.query<SessionStats, Record<string, never>>({
      query: () => '/stats',
      transformResponse: (response: { data: { stats: SessionStats } }) => response.data.stats,
      providesTags: ['SmokingSession'],
    }),
    getCigarSessions: builder.query<SmokingSession[], string>({
      query: (cigarId) => `/cigar/${cigarId}`,
      transformResponse: (response: { data: { sessions: SmokingSession[] } }) => response.data.sessions,
      providesTags: (_result, _error, cigarId) => [
        { type: 'SmokingSession', id: `cigar-${cigarId}` }
      ],
    }),
    getDrafts: builder.query<{ drafts: SmokingSession[] }, void>({
      query: () => '/drafts',
      transformResponse: (response: { data: { drafts: SmokingSession[] } }) => ({ drafts: response.data.drafts }),
      providesTags: ['SmokingSession'],
    }),
    saveDraft: builder.mutation<SmokingSession, { id: string; updates: UpdateSessionRequest }>({
      query: ({ id, updates }) => ({
        url: `/${id}/draft`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      invalidatesTags: ['SmokingSession'],
    }),
    startTimer: builder.mutation<SmokingSession, string>({
      query: (id) => ({
        url: `/${id}/timer/start`,
        method: 'PUT',
      }),
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      invalidatesTags: (_result, _error, id) => [{ type: 'SmokingSession', id }],
    }),
    stopTimer: builder.mutation<SmokingSession, string>({
      query: (id) => ({
        url: `/${id}/timer/stop`,
        method: 'PUT',
      }),
      transformResponse: (response: { data: { session: SmokingSession } }) => response.data.session,
      invalidatesTags: (_result, _error, id) => [{ type: 'SmokingSession', id }],
    }),
  }),
})

export const {
  useGetSessionsQuery,
  useGetSessionQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
  useGetSessionStatsQuery,
  useGetCigarSessionsQuery,
  useGetDraftsQuery,
  useSaveDraftMutation,
  useStartTimerMutation,
  useStopTimerMutation,
} = sessionApi
